import { useEffect, useState } from 'react';
import { createWithEqualityFn } from 'zustand/traditional';
import { useCurrentUserContext } from '@/contexts';
import { useAuthPopup } from '@/features/magic/extension/hooks/useAuthPopup';
import { useBroadcastReceiver } from '@/features/magic/extension/hooks/useBroadcast';
import { useIsExtension } from '@/features/magic/hooks/useIsExtension';
import {
  type LaunchOption,
  type LaunchParams,
  useLauncher,
} from '@/hooks/useLauncher';
import { useToast } from '@/ui';
import { getAuthUrl } from '../util';

type Arg = {
  forEntryId?: string;
  launch?: LaunchOption;
  launchParams?: LaunchParams;
  email?: string;
};

type AuthEntryState = {
  entryId: string | undefined;
  setEntryId: (entryId: string | undefined) => void;
};

const useAuthEntryStore = createWithEqualityFn<AuthEntryState>()((set) => ({
  entryId: undefined,
  setEntryId: (entryId) => set({ entryId }),
}));

export const useExtensionPopup = () => {
  const toast = useToast();
  const { currentUser, invalidate: invalidateUser } = useCurrentUserContext();
  const { isExtension } = useIsExtension();
  const { open: openPopup, isBlocked } = useAuthPopup();
  const { entryId: activeEntryId, setEntryId: setActiveEntryId } =
    useAuthEntryStore();
  const { setLaunch: handleLaunch } = useLauncher();
  const [forEntryId, setForEntryId] = useState<string | undefined>();
  const [launch, setLaunch] = useState<LaunchOption | undefined>();
  const [launchParams, setLaunchParams] = useState<LaunchParams | undefined>();

  useEffect(() => {
    if (isBlocked) {
      toast.error(
        'We tried to connect to Google Calendar in a popup, but it was blocked. Please enable popups and try again.'
      );
    }
  }, [isBlocked, toast]);

  useBroadcastReceiver('_ah_auth_success', () => {
    invalidateUser();

    if (activeEntryId === forEntryId && launch) {
      handleLaunch(launch, launchParams || {});
      setLaunch(undefined);
      setLaunchParams(undefined);
    }
  });

  useBroadcastReceiver('_ah_auth_failed', () => {
    toast.warning('Unable to connect to Google, permission not granted');
  });

  const handleExtensionGoogleAuth = ({
    forEntryId,
    email,
    launch,
    launchParams,
  }: Arg = {}) => {
    setActiveEntryId(forEntryId);
    setForEntryId(forEntryId);
    setLaunch(launch);
    setLaunchParams(launchParams);

    const href = getAuthUrl(currentUser, { isExtension, email }, undefined, {});
    openPopup({ href });
  };

  return {
    onAuth: handleExtensionGoogleAuth,
  };
};
